import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  payrollPayments,
  PAYOUT_KIND_LABELS,
  type PayrollPaymentItem,
  type PayoutMethod,
  type PayoutKind,
  type CashPayoutMode,
} from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { individualBankAccounts } from '@/api/hr';
import { refs, type PayrollAccountsPreviewBatchRow } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { closePayrollSheet } from '@/api/salary_deposits';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { ProjectField } from '@/components/ProjectSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  payroll_id: string;
  payment_method: PayoutMethod;  // спосіб-дефолт для рядків
  payout_kind: PayoutKind | '';  // '' = ще не обрано («вибрати»)
  bank_account_id: string;  // рахунок організації для безготівкових рядків (дефолт Основний)
  cash_register_id: string;  // ч.1341: каса для готівкових рядків (дефолт — єдина / з 1С)
  period_year: number;
  period_month: number;
  comment: string;
  project_id: string | null;  // ч.735: напрям
  cash_payout_mode: CashPayoutMode;  // ч.738
  items: PayrollPaymentItem[];
}

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const now = new Date();
const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: now.toISOString().slice(0, 16),
  payroll_id: '',
  payment_method: 'CASH',
  payout_kind: '',
  bank_account_id: '',
  cash_register_id: '',
  period_year: now.getFullYear(),
  period_month: now.getMonth() + 1,
  comment: '',
  project_id: null,
  cash_payout_mode: 'SHEET',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PayrollPaymentEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  // Предзаповнення зі сторінки «Борги по ЗП» (кнопка «оплатити» на документі):
  // ?org=&kind=&year=&month=&doc= → org/вид/період + автовибір документа.
  const [searchParams] = useSearchParams();
  const prefillOrg = searchParams.get('org');
  const prefillKind = searchParams.get('kind') as PayoutKind | null;
  const prefillDoc = searchParams.get('doc');
  const [prefillDocApplied, setPrefillDocApplied] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['payroll-payment', id],
    queryFn: () => payrollPayments.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.payroll_payments', form.organization_id, isNew);
  const v = useFormValidation();

  function onSave() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
      ['payout_kind', form.payout_kind, 'Оберіть вид виплати'],
    ])) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
      ['payout_kind', form.payout_kind, 'Оберіть вид виплати'],
    ])) return;
    createAndPost.mutate();
  }
  function onUpdateAndPost() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
      ['payout_kind', form.payout_kind, 'Оберіть вид виплати'],
    ])) return;
    updateAndPost.mutate();
  }
  const [error, setError] = useState<string | null>(null);

  const empQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id),
    enabled: !!form.organization_id,
  });

  // Банк-рахунки організації (для вибору рахунку безготівкових виплат).
  const bankAccQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id,
  });
  // Дефолт — Основний (is_main) рахунок, коли ще не обрано.
  useEffect(() => {
    if (!form.bank_account_id && bankAccQ.data && bankAccQ.data.length > 0) {
      const main = bankAccQ.data.find((a) => a.is_main) ?? bankAccQ.data[0];
      if (main) setForm((f) => f.bank_account_id ? f : { ...f, bank_account_id: main.id });
    }
  }, [bankAccQ.data]);

  // ч.1341: каси організації — для готівкових рядків (раніше каси не обрати, і при
  // кількох касах «створити РКО» завжди відповідало «Оберіть касу…»).
  const cashRegQ = useQuery({
    queryKey: ['cash-registers', form.organization_id],
    queryFn: () => refs.cashRegisters(form.organization_id),
    enabled: !!form.organization_id,
  });
  useEffect(() => {
    const list = cashRegQ.data ?? [];
    if (form.cash_register_id || list.length === 0) return;
    const imported = list.filter((c) => c.external_source);
    const pick = list.length === 1 ? list[0] : imported.length === 1 ? imported[0] : null;
    if (pick) setForm((f) => (f.cash_register_id ? f : { ...f, cash_register_id: pick.id }));
  }, [cashRegQ.data]);

  // «На основі нарахування»: проведені НЕОПЛАЧЕНІ документи обраного виду+періоду.
  // Залежить від org + вид + період (зміна будь-чого → перезавантаження списку).
  const unpaidDocsQ = useQuery({
    queryKey: ['unpaid-docs', form.organization_id, form.payout_kind, form.period_year, form.period_month],
    queryFn: () => payrollPayments.unpaidDocuments({
      organization_id: form.organization_id, payout_kind: form.payout_kind as PayoutKind,
      period_year: form.period_year, period_month: form.period_month,
    }),
    enabled: !!form.organization_id && !!form.payout_kind && isNew,
  });

  // Закладка «Рахунки обліку»: ефективний рах. розрахунків per-рядок (той самий
  // резолвер, що проведення) + override у рядку (settlement_account_code).
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const accountsPreviewKind =
    form.payout_kind === 'VACATION' ? 'VACATION'
      : (form.payout_kind === 'SICK_ORG' || form.payout_kind === 'SICK_PFU') ? 'SICK'
      : form.payout_kind === 'ADVANCE' ? 'ADVANCE' : 'SALARY';
  const accEmpIds = form.items.filter((it) => it.employee_id).map((it) => it.employee_id);
  const accountsQ = useQuery({
    queryKey: ['payment-accounts-preview', form.organization_id, accountsPreviewKind, accEmpIds.join(',')],
    queryFn: () => refs.payrollAccountRules.previewBatch({
      organization_id: form.organization_id,
      employee_ids: accEmpIds,
      payment_kind: accountsPreviewKind,
    }),
    enabled: docTab === 'accounts' && !!form.organization_id && accEmpIds.length > 0,
  });
  const accByEmp = useMemo(() => {
    const m = new Map<string, PayrollAccountsPreviewBatchRow>();
    accountsQ.data?.rows.forEach((r) => m.set(r.employee_id, r));
    return m;
  }, [accountsQ.data]);
  // ч.811: рахунки фізосіб працівників — коли їх кілька, рахунок обирається у
  // рядку; не обрано → «!» (виплата піде на основний, але бухгалтер має підтвердити).
  const empBankAccQ = useQuery({
    queryKey: ['employee-bank-accounts', accEmpIds.join(',')],
    queryFn: () => individualBankAccounts.byEmployees(accEmpIds),
    enabled: accEmpIds.length > 0,
  });
  const bankAccByEmp = useMemo(() => {
    const m = new Map<string, { id: string; iban: string; account_name: string | null; is_main: boolean; bank_name: string | null }[]>();
    empBankAccQ.data?.forEach((r) => m.set(r.employee_id, r.accounts));
    return m;
  }, [empBankAccQ.data]);

  useEffect(() => {
    if (isNew) {
      if (prefillOrg && prefillKind) {
        const y = Number(searchParams.get('year') || '');
        const m = Number(searchParams.get('month') || '');
        setForm((f) => f.organization_id ? f : {
          ...f, organization_id: prefillOrg, payout_kind: prefillKind,
          period_year: y || f.period_year, period_month: m || f.period_month,
        });
        return;
      }
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        payroll_id: d.payroll_id ?? '',
        payment_method: (d.payment_method as PayoutMethod) ?? 'CASH',
        payout_kind: ((d.payout_kind as string) === 'SALARY' ? 'MAIN' : d.payout_kind as PayoutKind) ?? 'MAIN',
        // рахунок з BANK-рядка (усі однакові) або порожньо.
        bank_account_id: d.items.find((it) => it.bank_account_id)?.bank_account_id ?? '',
        cash_register_id: d.items.find((it) => it.cash_register_id)?.cash_register_id ?? '',
        period_year: d.period_year ?? now.getFullYear(),
        period_month: d.period_month ?? (now.getMonth() + 1),
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        cash_payout_mode: d.cash_payout_mode ?? 'SHEET',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          employee_id: it.employee_id,
          amount: String(it.amount),
          payment_method: it.payment_method ?? null,
          cash_register_id: it.cash_register_id ?? null,
          bank_account_id: it.bank_account_id ?? null,
          recipient_iban: it.recipient_iban ?? null,
          recipient_bank_name: it.recipient_bank_name ?? null,
          settlement_account_code: it.settlement_account_code ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  // Автовибір документа з «Боргів по ЗП»: коли список «На основі» завантажився —
  // обрати переданий документ (заповнить рядки залишками боргу).
  useEffect(() => {
    if (!isNew || !prefillDoc || prefillDocApplied || !unpaidDocsQ.data) return;
    setPrefillDocApplied(true);
    fillFromDocument(prefillDoc);
  }, [isNew, prefillDoc, prefillDocApplied, unpaidDocsQ.data]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const total = form.items.reduce((s, e) => s + Number(e.amount || 0), 0);
  // ч.770: «закрити відомість» — депонувати невидане (готівкові рядки).
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeSel, setCloseSel] = useState<Set<string>>(new Set());
  const closeSheet = useMutation({
    mutationFn: () => closePayrollSheet(id!, [...closeSel]),
    onSuccess: (r) => { setCloseOpen(false); navigate(`/salary-deposits/${r.salary_deposit_id}`); },
    onError: (e) => setError(extractError(e)),
  });
  const cashRowsForClose = (docQuery.data?.items ?? []).filter(
    (it) => (it.payment_method || docQuery.data?.payment_method || 'CASH') === 'CASH');
  // ч.764: створення платіжних документів з «ще дії».
  const makeDocs = useMutation({
    mutationFn: (p: { kind: 'bank' | 'cash'; mode?: 'SHEET' | 'INDIVIDUAL' }) =>
      p.kind === 'bank' ? payrollPayments.createBankPayments(id!)
        : payrollPayments.createCashOrders(id!, p.mode ?? 'SHEET', form.cash_register_id || null),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['payroll-payment', id] }); },
    onError: (e) => setError(extractError(e)),
  });

  // Вибір документа «На основі»: рядки = його items (employee + сума до виплати).
  // Спосіб per-рядок = suggested (IBAN→BANK, інакше CASH); борг-підказка = accrued.
  function fillFromDocument(docId: string) {
    const doc = unpaidDocsQ.data?.documents.find((d) => d.id === docId);
    if (!doc) {
      setForm((f) => ({ ...f, payroll_id: docId, items: [] }));
      return;
    }
    setForm((f) => ({
      ...f,
      payroll_id: docId,
      items: doc.items.map((it, idx) => ({
        line_number: idx + 1,
        employee_id: it.employee_id,
        amount: it.amount,
        payment_method: it.suggested_method,  // каса/банк за наявністю IBAN
        accrued: it.amount,
      })),
    }));
  }



  const buildPayload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number,
    document_date: new Date(form.document_date).toISOString(),
    // payroll_id — FK на doc.payrolls. Несемо ЛИШЕ для MAIN/ADVANCE (документ реально
    // з payrolls). Для VACATION/SICK/COMPENSATION id — з інших таблиць → null (інакше FK).
    payroll_id: (form.payout_kind === 'MAIN' || form.payout_kind === 'ADVANCE'
                  || form.payout_kind === 'CIVIL')
      ? (form.payroll_id || null) : null,
    payment_method: form.payment_method,
    payout_kind: form.payout_kind as PayoutKind,  // непорожній (валідація onSave)
    period_year: form.period_year,
    period_month: form.period_month,
    comment: form.comment || null,
    project_id: form.project_id,
    cash_payout_mode: form.cash_payout_mode,
    // accrued — лише фронт-підказка, прибираємо перед відправкою (backend extra=forbid).
    // BANK-рядкам проставляємо обраний рахунок організації (якщо у рядку не заданий свій).
    items: form.items.map(({ accrued: _a, ...it }) => ({
      ...it,
      bank_account_id: it.payment_method === 'BANK'
        ? (it.bank_account_id || form.bank_account_id || null)
        : (it.bank_account_id ?? null),
      // ч.1341: каса документа — для рядків, що не йдуть на рахунок.
      cash_register_id: it.payment_method !== 'BANK'
        ? (it.cash_register_id || form.cash_register_id || null)
        : (it.cash_register_id ?? null),
    })),
  });

  const create = useMutation({
    mutationFn: () => payrollPayments.create(buildPayload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
      qc.setQueryData(['payroll-payment', doc.id], doc);  // кеш ДО navigate (без гонки)
      navigate(`/payroll-payments/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => payrollPayments.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-payment', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await payrollPayments.create(buildPayload());
      try {
        await payrollPayments.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/payroll-payments/${doc.id}`, { replace: true });
        throw e;
      }
      // Забрати СВIЖИЙ проведений документ (posted=true + payment_docs) і покласти
      // у кеш ДО navigate — інакше гонка: docQuery ще undefined → форма показує
      // непроведений create-doc, блок кас «не знайдено», isPosted=false.
      const fresh = await payrollPayments.get(doc.id);
      qc.setQueryData(['payroll-payment', doc.id], fresh);
      return fresh;
    },
    onSuccess: (fresh) => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
      navigate(`/payroll-payments/${fresh.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await payrollPayments.update(id!, buildPayload());
      await payrollPayments.post(id!);
      // Забрати СВIЖИЙ проведений документ і покласти у кеш СИНХРОННО — інакше
      // invalidate дає рефетч-гонку: форма показує непроведений стан + блок кас
      // «немає документа» до завершення рефетчу (той самий баг, що у createAndPost).
      const fresh = await payrollPayments.get(id!);
      qc.setQueryData(['payroll-payment', id], fresh);
      return fresh;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // ч.783: після розпроведення — що саме видалено (як у перерахуванні податків).
  const [deletedDocs, setDeletedDocs] = useState<{ cash: number; bank: number }>({ cash: 0, bank: 0 });
  const unpost = useMutation({
    mutationFn: async () => {
      const had = docQuery.data?.payment_docs ?? [];
      const fresh = await payrollPayments.unpost(id!);
      qc.setQueryData(['payroll-payment', id], fresh);
      setDeletedDocs({
        cash: had.filter((d) => d.doc_type === 'cash').length,
        bank: had.filter((d) => d.doc_type !== 'cash').length,
      });
      return fresh;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => payrollPayments.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
      navigate('/payroll-payments');
    },
  });

  function setItem(i: number, patch: Partial<PayrollPaymentItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        line_number: f.items.length + 1,
        employee_id: '', amount: '0',
      }],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const employeeName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.forEach((e) => m.set(e.id, e.code ? `${e.name} — ${e.code}` : e.name));
    return m;
  }, [empQ.data]);

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати виплату зарплати у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/payroll-payments')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.873 (новий дизайн, Закон #10): усі 5 «ще дій» (ч.764/735) →
  // «Повʼязані дії» правої колонки — дослівно.
  const railActions = [{
      // ч.764 (рішення власника): платіжні документи — явними діями; ч.1347 — зі
      // збереженої відомості, проведення не чекаємо (закриття — лише з проведеної).
      label: 'створити платіжні доручення',
      disabled: isNew || makeDocs.isPending,
      title: isNew ? 'Спершу збережіть відомість'
        : 'Окрема платіжка-чернетка кожному працівнику (безготівкові рядки) → Банк → Списання → збережені',
      onClick: () => makeDocs.mutate({ kind: 'bank' }),
    }, {
      label: 'створити РКО зведену',
      disabled: isNew || makeDocs.isPending,
      title: isNew ? 'Спершу збережіть відомість' : 'Один РКО на касу за всіма готівковими рядками (друк П-53)',
      onClick: () => makeDocs.mutate({ kind: 'cash', mode: 'SHEET' }),
    }, {
      label: 'створити РКО індивідуальну',
      disabled: isNew || makeDocs.isPending,
      title: isNew ? 'Спершу збережіть відомість' : 'Окремий РКО кожному працівнику (колеги не бачать чужих сум)',
      onClick: () => makeDocs.mutate({ kind: 'cash', mode: 'INDIVIDUAL' }),
    }, {
      label: 'закрити відомість (депонувати невидане)',
      disabled: !isPosted || cashRowsForClose.length === 0,
      title: !isPosted ? 'Спершу проведіть відомість'
        : cashRowsForClose.length === 0 ? 'У відомості немає готівкових рядків'
        : 'Позначити працівників, що не отримали готівку у строк: їх рядки знімаються з РКО, створюється «Депонування зарплати» (Дт 661 — Кт 662)',
      onClick: () => { setCloseSel(new Set()); setCloseOpen(true); },
    }, {
      // ч.735 (канон Закону #6): доп-дія у «ще дії», активна після збереження.
      label: 'створити податкові виплати',
      disabled: isNew || !docQuery.data,
      title: (isNew || !docQuery.data)
        ? 'Спершу збережіть документ — тоді можна створити податкові виплати'
        : 'Створити перерахування ПДФО/ВЗ/ЄСВ на основі цієї виплати',
      onClick: () => navigate(`/tax-remittances/new?from_payment=${id}`),
    }];
  // Прогрес — лише обовʼязкові: організація, дата, вид виплати; рядки — для проведення.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'kind', title: 'Вид виплати і нарахування', required: 1,
      filled: form.payout_kind ? 1 : 0 },
    { id: 'items', title: 'Виплати', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова виплата ЗП' : `Виплата № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/payroll-payments')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postDisabled={isNew
            ? (create.isPending || createAndPost.isPending)
            : (form.items.length === 0 || updateAndPost.isPending || update.isPending)}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          // ч.763 (рішення власника): варіанти видачі — у «друк ▾», окремими документами
          // з редактором друку (підпис/печатка, вкладення).
          printOptions={[
            { label: 'платіжна відомість П-53 (зведена, готівка)', onClick: () => navigate(`/payroll-payments/${id}/print?form=sheet`) },
            { label: 'індивідуальні відомості (готівка + розрахунковий листок)', onClick: () => navigate(`/payroll-payments/${id}/print?form=individual`) },
            { label: 'відомість на карткові рахунки (банк)', onClick: () => navigate(`/payroll-payments/${id}/print?form=bank`) },
          ]}
          entriesDoc={{ type: 'doc.payroll_payments', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {/* ч.784: статус платіжних документів — угорі, загальною кількістю
          (як «✓ проведено. Платіжних інструкцій: N» у перерахуванні податків). */}
      {isPosted && (() => {
        const pds = docQuery.data?.payment_docs ?? [];
        const bank = pds.filter((d) => d.doc_type !== 'cash').length;
        const cash = pds.length - bank;
        return (
          <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
            borderRadius: 6, marginBottom: '0.75rem' }}>
            {pds.length === 0 && 'Платіжних документів не створено — «Повʼязані дії» (платіжні доручення / РКО).'}
            {bank > 0 && (<>Створено платіжних інструкцій: {bank}{' '}
              <Link to="/bank-payments?tab=saved">дивитись</Link></>)}
            {bank > 0 && cash > 0 && <span style={{ margin: '0 0.6rem' }}>·</span>}
            {cash > 0 && (<>Створено РКО: {cash}{' '}
              <Link to="/cash-payments">дивитись</Link></>)}
          </div>
        );
      })()}
      {!isPosted && (deletedDocs.cash + deletedDocs.bank) > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
          border: '1px solid var(--badge-amber-bd)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
          Розпроведено.
          {deletedDocs.bank > 0 && ` Видалено платіжних інструкцій: ${deletedDocs.bank}.`}
          {deletedDocs.cash > 0 && ` Видалено РКО: ${deletedDocs.cash}.`}
          {' '}(чернетки, створені після проведення цієї відомості). Після повторного
          проведення створіть їх знову у «Повʼязаних діях»; номери будуть нові, привʼязки
          податкових платіжок — заново.
        </div>
      )}

      {closeOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setCloseOpen(false)}>
          <div className="card" style={{ width: 560, maxWidth: '95vw', padding: '1rem' }} onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Закрити відомість: хто НЕ отримав готівку</h3>
              <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setCloseOpen(false)}>закрити</button>
            </div>
            <TchTable>
              <thead><tr><th style={{ width: 36 }}></th><th>Працівник</th><th className="num" style={{ width: 130 }}>Сума</th></tr></thead>
              <tbody>
                {cashRowsForClose.map((it) => (
                  <tr key={it.employee_id}>
                    <td><input type="checkbox" checked={closeSel.has(it.employee_id)} style={{ width: 'auto' }}
                      onChange={(e) => setCloseSel((prev) => { const n = new Set(prev); if (e.target.checked) n.add(it.employee_id); else n.delete(it.employee_id); return n; })} /></td>
                    <td>{employeeName.get(it.employee_id) ?? it.employee_id}</td>
                    <td style={{ textAlign: 'right' }}>{Number(it.amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
              <button type="button" onClick={() => setCloseOpen(false)}>скасувати</button>
              <button type="button" className="primary" disabled={closeSel.size === 0 || closeSheet.isPending}
                onClick={() => closeSheet.mutate()}>депонувати ({closeSel.size})</button>
            </div>
          </div>
        </div>
      )}
      {v.banner()}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.payroll_payments', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(val) => setForm({ ...form, document_date: val })}
                 disabled={isPosted} />
        </div>
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Вид виплати і нарахування">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Вид виплати *</label>
          <select value={form.payout_kind}
                  onChange={(e) => setForm({
                    ...form,
                    payout_kind: e.target.value as PayoutKind | '',
                    // Зміна виду → скидаємо період (на поточний), документ і рядки.
                    period_year: now.getFullYear(),
                    period_month: now.getMonth() + 1,
                    payroll_id: '',
                    items: [],
                  })}
                  disabled={isPosted}>
            <option value="">— вибрати —</option>
            {(Object.entries(PAYOUT_KIND_LABELS) as Array<[PayoutKind, string]>)
              .map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={form.period_month}
                    onChange={(e) => setForm({ ...form, period_month: Number(e.target.value), payroll_id: '', items: [] })}
                    disabled={isPosted || !form.payout_kind} style={{ flex: 2 }}>
              {MONTHS.slice(1).map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
            <select value={form.period_year}
                    onChange={(e) => setForm({ ...form, period_year: Number(e.target.value), payroll_id: '', items: [] })}
                    disabled={isPosted || !form.payout_kind} style={{ flex: 1 }}>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label title="Проведені неоплачені документи обраного виду за період">Нарахування</label>
          <select value={form.payroll_id}
                  onChange={(e) => fillFromDocument(e.target.value)}
                  disabled={isPosted || !form.payout_kind}>
            <option value="">
              {!form.payout_kind ? '— спершу оберіть вид виплати —'
                : unpaidDocsQ.isFetching ? '— завантаження…'
                : (unpaidDocsQ.data?.documents.length ?? 0) === 0
                  ? '— немає неоплачених документів цього виду за період —'
                  : '— оберіть документ —'}
            </option>
            {unpaidDocsQ.data?.documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.document_number}
                {d.employee_name ? ` — ${d.employee_name}` : ` — ${d.employee_count} осіб`}
                {' '}({Number(d.total).toFixed(2)} грн до виплати)
                {d.posted === false ? ' — не проведено' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label title="Каса, з якої видається готівка (рядки без рахунку працівника)">Каса виплати</label>
          <select value={form.cash_register_id}
                  onChange={(e) => setForm({ ...form, cash_register_id: e.target.value })}
                  disabled={isPosted && !!docQuery.data?.items.some((it) => it.cash_register_id)}>
            <option value="">— оберіть касу —</option>
            {cashRegQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {c.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label title="Рахунок організації для безготівкових рядків">Банківський рахунок списання</label>
          <select value={form.bank_account_id}
                  onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— оберіть рахунок —</option>
            {bankAccQ.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.is_main ? '★ Основний — ' : ''}{a.account_name || a.iban}
              </option>
            ))}
          </select>
        </div>
      </div>

      {form.payout_kind === 'SICK_PFU' && (
        <p style={{ marginTop: '0.5rem', padding: '0.6rem 0.8rem', background: 'var(--badge-amber-bg)',
                    border: '1px solid var(--badge-amber-bd)', borderRadius: 6,
                    color: 'var(--badge-amber-fg)', fontSize: '0.85rem' }}>
          ⚠ Лікарняний за рахунок Фонду виплачується зі <strong>спецрахунку ПФУ</strong> —
          оберіть саме його у полі «Банківський рахунок списання» вище (не основний р/р).
        </p>
      )}

      </DocBlock>

      <DocBlock index={3} title="Виплати"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={addRow}>+ додати рядок</button>
        ) : undefined}>
      {/* ч.735: вкладки за каноном — «Виплати» зліва, «Рахунки обліку» справа
          (дт-кт · історія · звʼязані — в інфо-рядку назви через DocumentToolbar). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={docTab === 'calc' ? 'active' : ''}
            onClick={() => setDocTab('calc')}>Виплати ({form.items.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={docTab === 'accounts' ? 'active' : ''}
            onClick={() => setDocTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {docTab === 'accounts' && (
        <div>
          <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0.5rem 0' }}>
            Рах. розрахунків рядка: порожньо — автоматичний (правило обліку /
            персональний / ЦПУ 6851 / стандартний); введене значення — застосується
            саме воно у проводці та погашенні боргу.
          </p>
          <TchTable>
            <thead>
              <tr>
                <th>Співробітник</th>
                <th style={{ width: 130, textAlign: 'right' }}>Сума</th>
                <th style={{ width: 200 }}>Рах. розрахунків (Дт)</th>
                <th style={{ width: 140 }}>Кт (спосіб)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.employee_id).map((it, i) => {
                const prev = accByEmp.get(it.employee_id);
                const auto = prev?.payroll.account_code ?? '…';
                const src = prev?.payroll.source;
                const method = it.payment_method ?? form.payment_method;
                return (
                  <tr key={i}>
                    <td>{employeeName.get(it.employee_id) ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(it.amount || 0))}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input value={it.settlement_account_code ?? ''} disabled={isPosted}
                          placeholder={auto}
                          style={{ width: 90 }}
                          onChange={(e) => setItem(form.items.indexOf(it), {
                            settlement_account_code: e.target.value || null,
                          })} />
                        {/* «інд» — лише де рахунок НЕ з документа (правило/картка/ЦПУ). */}
                        {!it.settlement_account_code && src
                          && ['rule', 'card', 'civil_default'].includes(src) && (
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}
                            title={src === 'rule' ? 'правило обліку'
                              : src === 'card' ? 'персональний рахунок працівника'
                              : 'ЦПУ-дефолт'}>
                            інд
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {method === 'BANK' ? '311 (банк)' : '301 (каса)'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          {accEmpIds.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Заповніть рядки на закладці «Виплати».
            </p>
          )}
        </div>
      )}

      <div style={{ display: docTab === 'calc' ? 'block' : 'none' }}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Співробітник</th>
            <th style={{ width: 120 }}>Спосіб</th>
            <th style={{ width: 260 }} title="Рахунок фізособи; кілька рахунків — оберіть у рядку">Рахунок</th>
            <th style={{ width: 150 }}>Сума виплати</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                {/* ч.736: пошуковий вибір працівника (SearchSelect) замість select. */}
                <SearchSelect
                  items={(empQ.data ?? []).map((e) => ({ id: e.id, label: e.name, hint: e.code ?? null }))}
                  value={it.employee_id}
                  onChange={(id) => setItem(i, { employee_id: id })}
                  disabled={isPosted}
                  placeholder="— оберіть працівника —"
                />
              </td>
              <td>
                {/* Спосіб per-рядок. Порожньо/CASH = каса (дефолт для тих, у кого
                    немає банк-рахунку). Підтягування ставить BANK тим, у кого є IBAN. */}
                <select value={it.payment_method ?? 'CASH'}
                        onChange={(e) => setItem(i, {
                          payment_method: e.target.value as PayoutMethod,
                        })}
                        disabled={isPosted} style={{ width: '100%' }}>
                  <option value="CASH">Каса</option>
                  <option value="BANK">Банк</option>
                </select>
              </td>
              <td>
                {(() => {
                  const method = it.payment_method ?? form.payment_method;
                  if (method !== 'BANK' || !it.employee_id) return <span style={{ color: 'var(--muted)' }}>—</span>;
                  const accs = bankAccByEmp.get(it.employee_id) ?? [];
                  if (accs.length === 0) {
                    return <span style={{ color: 'var(--badge-red-fg)', fontSize: '0.8rem' }}
                      title="У картці фізособи немає рахунку — додайте, інакше платіжка буде без IBAN">немає рахунку</span>;
                  }
                  if (accs.length === 1) {
                    const only = accs[0]!;
                    return <span style={{ fontSize: '0.8rem' }} title={only.bank_name ?? undefined}>{only.iban}</span>;
                  }
                  const chosen = it.recipient_iban ?? '';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {!chosen && (
                        <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 700 }}
                          title="У працівника кілька рахунків — оберіть, на який платити (інакше піде на основний)">!</span>
                      )}
                      <select value={chosen} disabled={isPosted} style={{ width: '100%' }}
                        onChange={(e) => {
                          const a = accs.find((x) => x.iban === e.target.value);
                          setItem(i, { recipient_iban: a?.iban ?? null, recipient_bank_name: a?.bank_name ?? null });
                        }}>
                        <option value="">— оберіть рахунок —</option>
                        {accs.map((a) => (
                          <option key={a.id} value={a.iban}>
                            {a.is_main ? '★ ' : ''}{a.iban}{a.account_name ? ` · ${a.account_name}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </td>
              <td>
                <input type="number" step="0.01" value={it.amount}
                       onChange={(e) => setItem(i, { amount: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
                {it.accrued && Number(it.accrued) - Number(it.amount || 0) > 0.005 && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--badge-amber-fg)', textAlign: 'right' }}
                       title="Залишок неоплаченого піде у борг (підтягнеться через «Нарахування (борг)»)">
                    у борг: {(Number(it.accrued) - Number(it.amount || 0)).toFixed(2)}
                  </div>
                )}
              </td>
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      </div>
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.payroll_payments" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
